// Svelte 5 no longer needs svelte/store imports for basic reactivity
import online from "../configs/status.js";
import { v4 as uuidv4 } from 'uuid';

/* Firebase */
import firebase from '@app/configs/firebase.js'; //Required even if not used.
import db from '@app/configs/firestore.js';

// Preset types
export const PRESET_TYPES = {
    SUBTITLE: 'subtitle',
    TRANSCRIPT: 'transcript',
    AUDIO_DESCRIPTION: 'audio-description',
    CCSL: 'ccsl',
    DIALOGUE_LIST: 'dialogue-list',
    VIDEO: 'video'
};

// Function to initialize preset state from localStorage
function initializePresetState() {
    const presetData = JSON.parse(localStorage.getItem("cc-export-presets")) || [];
    
    // Sort the presets by name
    presetData.sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '');
    });
    
    return presetData;
}

// Create reactive state for export presets, initialized from localStorage
export const presetState = $state(initializePresetState());

// Track if presets have been loaded from Firebase
let presetsLoadedFromFirebase = false;

// Function to load presets from database or localStorage
export async function loadPresets(userId) {
    let presetData = [];
    
    // Use local storage instead of firebase when online is set to false
    if (online && userId) {
        try {
            let snapshot = await db.collection("users").doc(userId).collection("presets").get();
            snapshot.forEach(doc => {
                presetData = [...presetData, doc.data()];
            });
            presetsLoadedFromFirebase = true;
        } catch (error) {
            console.error("Error loading presets from Firebase:", error);
            // Fallback to localStorage on error
            presetData = JSON.parse(localStorage.getItem("cc-export-presets")) || [];
        }
    } else {
        presetData = JSON.parse(localStorage.getItem("cc-export-presets")) || [];
    }
    
    // Sort the presets by name
    presetData.sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '');
    });

    // Update the state by emptying the array and adding new items
    presetState.length = 0;
    presetData.forEach(item => presetState.push(item));
}

// Function to get presets by type
export function getPresetsByType(type) {
    return presetState.filter(preset => preset.type === type);
}

// Function to create a new preset
export async function createPreset(presetInfo, userId) {
    // Ensure the preset has an ID
    if (!presetInfo.id) {
        presetInfo.id = uuidv4();
    }
    
    // Add timestamp
    presetInfo.createdAt = new Date().toISOString();
    presetInfo.updatedAt = new Date().toISOString();
    
    // Use local storage instead of firebase when online is set to false
    if (online && userId) {
        try {
            await db.collection("users").doc(userId).collection("presets").doc(presetInfo.id).set(presetInfo);
        } catch (error) {
            console.error("Error saving preset to Firebase:", error);
            // Fallback to localStorage on error
            const currentPresets = JSON.parse(localStorage.getItem("cc-export-presets")) || [];
            currentPresets.push(presetInfo);
            localStorage.setItem("cc-export-presets", JSON.stringify(currentPresets));
        }
    } else {
        const currentPresets = JSON.parse(localStorage.getItem("cc-export-presets")) || [];
        currentPresets.push(presetInfo);
        localStorage.setItem("cc-export-presets", JSON.stringify(currentPresets));
    }

    // Add the new item to the array without reassigning
    presetState.push(presetInfo);
    
    return presetInfo;
}

// Function to remove a preset
export async function removePreset(presetId, userId) {
    // Use local storage instead of firebase when online is set to false
    if (online && userId) {
        try {
            await db.collection("users").doc(userId).collection("presets").doc(presetId).delete();
        } catch (error) {
            console.error("Error deleting preset from Firebase:", error);
        }
    }
    
    // Always update localStorage to keep it in sync
    const updatedPresets = presetState.filter(preset => preset.id !== presetId);
    localStorage.setItem("cc-export-presets", JSON.stringify(updatedPresets));
    
    // Filter in place without reassigning the state
    const itemsToKeep = presetState.filter(preset => preset.id !== presetId);
    presetState.length = 0;
    itemsToKeep.forEach(item => presetState.push(item));
}

// Function to update an existing preset
export async function updatePreset(presetInfo, userId) {
    // Update timestamp
    presetInfo.updatedAt = new Date().toISOString();
    
    // Use local storage instead of firebase when online is set to false
    if (online && userId) {
        try {
            await db.collection("users").doc(userId).collection("presets").doc(presetInfo.id).set(presetInfo);
        } catch (error) {
            console.error("Error updating preset in Firebase:", error);
        }
    }
    
    let presetIndex = presetState.findIndex(preset => preset.id === presetInfo.id);

    if (presetIndex !== -1) {
        // Update in place without reassigning the entire array
        presetState[presetIndex] = presetInfo;
    } else {
        // If not found, add it
        presetState.push(presetInfo);
    }
    
    // Always update localStorage to keep it in sync
    localStorage.setItem("cc-export-presets", JSON.stringify(presetState));
}

// Function to export presets as JSON
export function exportPresetsAsJson(presetIds = null) {
    let presetsToExport;
    
    if (presetIds && Array.isArray(presetIds)) {
        presetsToExport = presetState.filter(preset => presetIds.includes(preset.id));
    } else {
        presetsToExport = [...presetState];
    }
    
    return JSON.stringify(presetsToExport, null, 2);
}

// Function to import presets from JSON
export async function importPresetsFromJson(jsonString, userId) {
    try {
        const importedPresets = JSON.parse(jsonString);
        
        if (!Array.isArray(importedPresets)) {
            throw new Error("Invalid preset format: expected an array");
        }
        
        const results = {
            success: [],
            failed: [],
            duplicates: []
        };
        
        for (const preset of importedPresets) {
            // Validate preset structure
            if (!preset.type || !preset.config) {
                results.failed.push({
                    preset,
                    reason: "Missing required fields (type or config)"
                });
                continue;
            }
            
            // Check for duplicate names
            const existingPreset = presetState.find(p => 
                p.name === preset.name && p.type === preset.type
            );
            
            if (existingPreset) {
                results.duplicates.push(preset);
                // Generate a new name for the duplicate
                preset.name = `${preset.name} (imported)`;
            }
            
            // Create new ID for imported preset
            const newPreset = {
                ...preset,
                id: uuidv4(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            
            try {
                await createPreset(newPreset, userId);
                results.success.push(newPreset);
            } catch (error) {
                results.failed.push({
                    preset,
                    reason: error.message
                });
            }
        }
        
        return results;
    } catch (error) {
        throw new Error(`Failed to import presets: ${error.message}`);
    }
}

// Function to get a preset by ID
export function getPresetById(presetId) {
    return presetState.find(preset => preset.id === presetId);
}
